/**
 * calendarEvents — pure aggregation of Agenda calendar events. Kept free of any
 * hook/context/Supabase import so it is trivially unit-testable.
 *
 * Timezone note (America/Sao_Paulo): appointments store `starts_at` as a real
 * instant (local wall-clock → toISOString), while service orders and finance
 * entries store date-only values. `buildCalendarEvents` normalizes date-only
 * values to a LOCAL Date that lands on the true calendar day, so day-grouping
 * via `dayKeyOf` is correct. Never parse `scheduled_date` or `due_date` with
 * `new Date(value)` directly — that shifts it to the previous day under UTC-3.
 */
import { computeFinanceStatus, todayIsoDate } from '@/lib/financeStatus';

export type CalendarEventKind = 'appointment' | 'service_order' | 'finance_entry' | 'birthday';

export interface CalendarEvent {
  id: string;
  kind: CalendarEventKind;
  title: string;
  /**
   * A LOCAL Date that lands on the event's true calendar day (and, for timed
   * appointments, its wall-clock time). Use `dayKeyOf(date)` to group by day.
   */
  date: Date;
  allDay: boolean;
  status: string;
  /** The original underlying row. */
  ref: any;
}

/** Local 'YYYY-MM-DD' key for a Date, used to group events by calendar day. */
export function dayKeyOf(date: Date): string {
  return todayIsoDate(date);
}

/**
 * Parse a date-only 'YYYY-MM-DD' (the leading part of an ISO string) into a
 * LOCAL midnight Date on that exact calendar day — timezone-agnostic. Used for
 * service orders, whose `scheduled_date` is stored as UTC-midnight of a
 * date-only value; `new Date(iso)` would shift it a day under UTC-3.
 */
export function localDateFromIsoDatePart(iso: string): Date {
  const datePart = String(iso).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return new Date(Number.NaN);

  const [y, m, d] = datePart.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  if (
    date.getFullYear() !== y ||
    date.getMonth() !== m - 1 ||
    date.getDate() !== d
  ) {
    return new Date(Number.NaN);
  }
  return date;
}

function appointmentTitle(a: any): string {
  const t = (a?.title ?? '').trim();
  if (t) return t;
  const client = (a?.client_name ?? '').trim();
  if (client) return client;
  return 'Compromisso';
}

function serviceOrderTitle(so: any): string {
  const t = (so?.title ?? '').trim();
  if (t) return t;
  const client = (so?.client_name ?? '').trim();
  if (client) return client;
  if (so?.sequence_id) return `OS #${so.sequence_id}`;
  return 'Ordem de serviço';
}

function financeEntryTitle(entry: any): string {
  const description = (entry?.description ?? '').trim();
  if (description) return description;
  return entry?.kind === 'expense' ? 'Conta a pagar' : 'Conta a receber';
}

/**
 * Merge appointments + scheduled service orders + finance due dates into one
 * calendar event list. Pure and side-effect free (testable). Cancelled and paid
 * rows are kept — the UI decides how to render them and history remains visible.
 */
export function buildCalendarEvents(
  appointments: any[],
  serviceOrders: any[],
  financeEntries: any[],
  /**
   * Clientes com `birth_date`. Opcional para não quebrar chamador antigo — mas
   * o app tem UM só, e ele passa.
   */
  clients: any[] = [],
  /**
   * Anos em que projetar os aniversários. O aniversário é o único evento
   * RECORRENTE aqui: a coluna guarda o ano de NASCIMENTO, e sem esta lista não
   * há como saber em que ano desenhar (ver `buildBirthdayEvents`).
   */
  years: number[] = [new Date().getFullYear()],
): CalendarEvent[] {
  const events: CalendarEvent[] = [];

  for (const a of appointments ?? []) {
    if (!a?.starts_at) continue;
    const date = new Date(a.starts_at);
    if (Number.isNaN(date.getTime())) continue;
    // Appointments store a real instant (local wall-clock → ISO), so a plain
    // `new Date` yields the correct local day and time.
    events.push({
      id: String(a.id),
      kind: 'appointment',
      title: appointmentTitle(a),
      date,
      allDay: a.all_day === true,
      status: a.status ?? 'scheduled',
      ref: a,
    });
  }

  for (const so of serviceOrders ?? []) {
    if (!so?.scheduled_date) continue;
    // Service order scheduled dates are date-only (UTC-midnight); normalize to a
    // local Date on that calendar day so grouping matches how the OS module
    // itself displays the date (ISO date-part slice).
    const date = localDateFromIsoDatePart(so.scheduled_date);
    if (Number.isNaN(date.getTime())) continue;
    events.push({
      id: String(so.id),
      kind: 'service_order',
      title: serviceOrderTitle(so),
      date,
      allDay: true,
      status: so.status ?? 'pending',
      ref: so,
    });
  }

  for (const entry of financeEntries ?? []) {
    if (!entry?.due_date) continue;
    // `due_date` is a Postgres DATE. Parsing it as a UTC instant would put the
    // deadline on the previous calendar day in Brazil.
    const date = localDateFromIsoDatePart(entry.due_date);
    if (Number.isNaN(date.getTime())) continue;
    events.push({
      id: String(entry.id),
      kind: 'finance_entry',
      title: financeEntryTitle(entry),
      date,
      allDay: true,
      status:
        entry.computed_status ??
        computeFinanceStatus(entry.status, entry.due_date),
      ref: entry,
    });
  }

  for (const event of buildBirthdayEvents(clients, years)) events.push(event);

  // Earliest first; within a day, all-day/earlier times bubble up.
  events.sort((x, y) => x.date.getTime() - y.date.getTime());
  return events;
}

/**
 * ANIVERSÁRIOS — o único evento RECORRENTE do calendário, e é isso que o torna
 * diferente dos outros três.
 *
 * `clients.birth_date` guarda a data de NASCIMENTO (com o ano de nascimento);
 * o que a Agenda mostra é o aniversário PROJETADO nos anos visíveis. Por isso a
 * função recebe `years`: sem eles não há como saber em que ano desenhar, e usar
 * o ano de nascimento poria o evento em 1987.
 *
 * ⚠️ 29 DE FEVEREIRO é o caso que quebra em silêncio. `new Date(2027, 1, 29)`
 * não estoura: ele ROLA para 1º de março, e o aniversário apareceria no dia
 * errado sem erro nenhum. Em ano não bissexto o app mostra em **28 de
 * fevereiro** — que é a convenção civil brasileira (art. 132 §3º do Código
 * Civil trata prazo em mês sem o dia equivalente pelo último dia do mês) e,
 * mais importante, é uma escolha DELIBERADA em vez de um efeito colateral.
 */
function buildBirthdayEvents(clients: any[], years: number[]): CalendarEvent[] {
  const out: CalendarEvent[] = [];
  const anos = [...new Set(years)].filter((y) => Number.isInteger(y));
  if (anos.length === 0) return out;

  for (const client of clients ?? []) {
    if (!client?.birth_date) continue;
    const nascimento = localDateFromIsoDatePart(client.birth_date);
    if (Number.isNaN(nascimento.getTime())) continue;

    const mes = nascimento.getMonth();
    const dia = nascimento.getDate();

    for (const ano of anos) {
      // Bissexto: 29/02 num ano comum cai para 28/02 (ver o comentário acima).
      const ultimoDiaDoMes = new Date(ano, mes + 1, 0).getDate();
      const date = new Date(ano, mes, Math.min(dia, ultimoDiaDoMes));

      out.push({
        // O id carrega o ANO porque o mesmo cliente gera um evento por ano
        // visível — sem isso, dois anos na tela colidiriam na chave do React.
        id: `birthday:${client.id}:${ano}`,
        kind: 'birthday',
        title: `Aniversário de ${String(client.name ?? '').trim() || 'cliente'}`,
        date,
        allDay: true,
        status: 'birthday',
        ref: client,
      });
    }
  }

  return out;
}
